import { NewItem } from '@/domain/new-item'
import { DeleteIcon } from '@itsrever/design-system'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { removeReturnedItem } from '@/redux/return/returnSlice'
import { usePostHog } from '@/application/use-posthog'
import { useCart } from '@/application/use-cart/use-cart'
import { QuantitySelector } from '@/ui/components/quantity-selector'

interface CartItemProps {
    item: NewItem
    index: number
    returningItemId: string
}

export const CartItem: React.FC<CartItemProps> = ({
    item,
    index,
    returningItemId
}) => {
    const dispatch = useAppDispatch()
    const { captureEventPosthog } = usePostHog()

    const variant = item.productVariant
    const { removeCartItem, updateCartItemQuantity } = useCart()
    const newItems = useAppSelector((state) => state.return.newItems)
    const returnedItems = useAppSelector((state) => state.return.returnedItems)
    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isHoff = settings.ecommerce_group === 'hoff'

    const handleClickRemove = () => {
        captureEventPosthog('FC - Item deleted from cart', {
            ecommerceId: settings.ecommerce_id
        })
        removeCartItem(item)
        if (item.returnedItemId !== returningItemId) {
            const associatedItems = newItems.filter(
                (i) => i.returnedItemId === item.returnedItemId
            )
            if (associatedItems.length <= 1) {
                const returnedItem = returnedItems.find(
                    (returnedItem) =>
                        returnedItem.lineItemId === item.returnedItemId
                )
                if (returnedItem) {
                    dispatch(
                        removeReturnedItem({
                            lineItemId: returnedItem.lineItemId
                        })
                    )
                }
            }
        }
    }
    const onUpdateCounter = (newQuantity: number) => {
        updateCartItemQuantity(item, newQuantity)
    }

    const isMisako = settings.ecommerce_group === 'misako'

    if (isHoff) {
        return (
            <div className="flex items-center border-b-1 border-solid border-black bg-white py-3">
                <img
                    src={variant.imageUrl}
                    alt={variant.variantName}
                    className={` mr-2 h-20 w-16 rounded-md object-cover`}
                />

                <div className="flex w-full items-center justify-between gap-4 px-2">
                    <div>
                        <p className="mb-1 flex items-center gap-2 font-haas text-base text-neutral-900">
                            <span className="font-haas">
                                {variant.productName}
                            </span>
                        </p>
                        <p className="font-haas font-light text-neutral-600">
                            {variant.variantName}
                        </p>
                    </div>
                    <div className="mr-1 flex flex-col items-end justify-end gap-3">
                        <button
                            data-testid={'remove-item-' + index}
                            onClick={handleClickRemove}
                            className="flex items-center justify-center rounded p-1 text-neutral-500 "
                        >
                            <svg
                                role="presentation"
                                width="18"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M7.8445 1.4629C8.453 1.4629 8.916 1.4609 9.306 1.5049C9.696 1.5489 10.0535 1.6459 10.32 1.9134C10.587 2.1804 10.684 2.5374 10.7275 2.9274C10.7585 3.2004 10.7645 3.5314 10.767 3.9004H12.157C12.184 3.8959 12.212 3.8939 12.2395 3.8939C12.2635 3.8944 12.287 3.8964 12.31 3.9004H13.6945C13.825 3.8984 13.951 3.9494 14.0435 4.0409C14.1365 4.1324 14.189 4.2574 14.189 4.3879C14.189 4.5184 14.1365 4.6434 14.0435 4.7354C13.951 4.8269 13.825 4.8774 13.6945 4.8754H12.7195V11.2129C12.7195 11.9429 12.721 12.4979 12.669 12.9564C12.6175 13.4149 12.505 13.8174 12.207 14.1149C11.9095 14.4124 11.507 14.5244 11.0485 14.5759C10.59 14.6274 10.036 14.6254 9.307 14.6254H6.382C5.652 14.6254 5.097 14.6269 4.6385 14.5749C4.18 14.5229 3.7775 14.4109 3.48 14.1129C3.1825 13.8154 3.0705 13.4129 3.019 12.9544C2.9685 12.5039 2.9695 11.9574 2.9695 11.2469C2.969 11.2359 2.9685 11.2244 2.9685 11.2129L2.9695 4.8754H1.9945C1.864 4.8774 1.738 4.8269 1.645 4.7354C1.5525 4.6434 1.5 4.5184 1.5 4.3879C1.5 4.2574 1.5525 4.1324 1.645 4.0409C1.738 3.9494 1.864 3.8984 1.9945 3.9004H3.3815C3.409 3.8959 3.437 3.8934 3.4645 3.8939C3.488 3.8944 3.512 3.8964 3.535 3.9004H4.9215C4.922 3.4784 4.925 3.1184 4.9625 2.8179C5.0075 2.4569 5.115 2.1099 5.387 1.8649C5.659 1.6194 6.002 1.5399 6.3875 1.5009C6.773 1.4619 7.236 1.4629 7.8445 1.4629ZM7.8445 2.4379C7.2365 2.4379 6.7865 2.4399 6.487 2.4704C6.187 2.5009 6.0735 2.5594 6.04 2.5894C6.007 2.6189 5.9615 2.6829 5.93 2.9389C5.9035 3.1509 5.898 3.4879 5.8965 3.9004H9.791C9.788 3.5469 9.7815 3.2404 9.7585 3.0359C9.7255 2.7409 9.669 2.6409 9.631 2.6029C9.5925 2.5644 9.4915 2.5074 9.1965 2.4744C8.9015 2.4409 8.4525 2.4379 7.8445 2.4379ZM11.7445 4.8754H10.364C10.3115 4.8844 10.2575 4.8844 10.205 4.8754H3.9445L3.9435 11.1799C3.9445 11.1909 3.9445 11.2019 3.9445 11.2134C3.9445 11.9419 3.9475 12.4829 3.9885 12.8459C4.0295 13.2094 4.1005 13.3564 4.1695 13.4249C4.238 13.4939 4.3845 13.5659 4.748 13.6069C5.1115 13.6479 5.6525 13.6509 6.382 13.6509H9.307C10.036 13.6509 10.5765 13.6479 10.94 13.6069C11.3035 13.5659 11.45 13.4949 11.519 13.4259C11.588 13.3569 11.6595 13.2109 11.7005 12.8469C11.7415 12.4834 11.7445 11.9429 11.7445 11.2134V4.8754Z"
                                    fill="currentColor"
                                ></path>
                            </svg>
                        </button>
                        <QuantitySelector
                            value={item.quantity}
                            onValueChange={onUpdateCounter}
                            minValue={1}
                            maxValue={
                                item.productVariant.inventoryQuantity ?? 1
                            }
                        />
                    </div>
                </div>
            </div>
        )
    }

    if (isMisako) {
        return (
            <div className="mt-3 flex gap-4 bg-white py-1 md:py-0">
                <img
                    src={variant.imageUrl}
                    alt={variant.variantName}
                    className={`mr-2 h-40 w-32 scale-110 object-cover transition-all hover:rotate-0`}
                />
                <div className="flex w-full justify-between gap-4 px-2">
                    <div>
                        <p className="mb-1 line-clamp-2 flex max-w-72 items-center gap-2 text-sm font-[300] tracking-wider text-neutral-900 [font-family:Inter]">
                            {variant.productName}
                        </p>
                        <p className="text-[13px] font-light tracking-wide text-neutral-500 [font-family:Inter]">
                            {variant.variantName}
                        </p>
                        <div className="mr-1  mt-10 flex gap-3">
                            <QuantitySelector
                                value={item.quantity}
                                onValueChange={onUpdateCounter}
                                minValue={1}
                                maxValue={
                                    item.productVariant.inventoryQuantity ?? 1
                                }
                            />
                            <button
                                data-testid={'remove-item-' + index}
                                onClick={handleClickRemove}
                                className="flex items-center justify-center rounded p-1 text-neutral-400 hover:bg-neutral-100"
                            >
                                <svg
                                    width="20px"
                                    height="20px"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    color="#000000"
                                >
                                    <path
                                        d="M20 9L18.005 20.3463C17.8369 21.3026 17.0062 22 16.0353 22H7.96474C6.99379 22 6.1631 21.3026 5.99496 20.3463L4 9"
                                        stroke="#000000"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M21 6L15.375 6M3 6L8.625 6M8.625 6V4C8.625 2.89543 9.52043 2 10.625 2H13.375C14.4796 2 15.375 2.89543 15.375 4V6M8.625 6L15.375 6"
                                        stroke="#000000"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex items-center rounded-lg bg-white py-1 shadow-sm md:py-0">
            <img
                src={variant.imageUrl}
                alt={variant.variantName}
                className={`mr-2 size-16 ${
                    index % 2 === 0 ? 'rotate-3' : '-rotate-3'
                } scale-110 rounded-xl object-cover shadow-lg transition-all hover:rotate-0`}
            />

            <div className="flex w-full items-center justify-between gap-4 px-2">
                <div>
                    <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-neutral-900">
                        <span>{variant.productName}</span>
                    </h3>
                    <p className="font-light text-neutral-600">
                        {variant.variantName}
                    </p>
                </div>
                <div className="mr-1 flex gap-3">
                    <QuantitySelector
                        value={item.quantity}
                        onValueChange={onUpdateCounter}
                        minValue={1}
                        maxValue={item.productVariant.inventoryQuantity ?? 1}
                    />
                    <button
                        data-testid={'remove-item-' + index}
                        onClick={handleClickRemove}
                        className="flex items-center justify-center rounded p-1 text-neutral-400 hover:bg-neutral-100"
                    >
                        <DeleteIcon scale={1.1} />
                    </button>
                </div>
            </div>
        </div>
    )
}
